import {createSelector} from '@reduxjs/toolkit'

import {includes, some} from '@shared/helpers/collection'
import {eq} from '@shared/helpers/lang'

import {
    APIStatus,
    Channel,
    DiconEntry,
    OrderEntry,
    OrderPhase,
    OrderSheetPageSet,
    PageSet,
    PaymentDetailEntry,
    ScheduledOrderEntry,
} from '@web/core/constants'

import {RootState} from './interfaces'
import {entryToServiceSequence} from './utils'

export const commonSelector = (state: RootState) => state.common

const PAYMENT_SHEET_PARTNERS_OLD_PATH = '/payments/auth-pay'

/** Page Set Info selector */
export const pageSetInfoSelector = createSelector(
    (state: RootState) => state.common.pageSet,
    (pageSet) => {
        const eqPageSet = eq(pageSet)
        const pageSetIncludes = includes(pageSet)
        const orderSheetPageSet = (function () {
            if (eqPageSet(PageSet.MALL_CHECKOUT)) {
                return OrderSheetPageSet.MALL
            }
            if (eqPageSet(PageSet.EASY_BOOK_CHECKOUT)) {
                return OrderSheetPageSet.EASYBOOKING
            }
            if (eqPageSet(PageSet.SELLER_CHECKOUT)) {
                return OrderSheetPageSet.SELLER
            }
            if (eqPageSet(PageSet.FAMILY_PAY_CHECKOUT)) {
                return OrderSheetPageSet.FAMILY_PAY
            }
            return null
        })()
        return {
            pageSet,
            /** saleChannelGroup 와 동일한 기능 추후 pageSet 으로 대체 */
            orderSheetPageSet,
            /** 주문형 주문서 여부 */
            isOrderSheetCheckoutGroup: pageSetIncludes([
                PageSet.MALL_CHECKOUT,
                PageSet.EASY_BOOK_CHECKOUT,
                PageSet.SELLER_CHECKOUT,
                PageSet.FAMILY_PAY_CHECKOUT,
            ]),

            /** 디컨 단건결제 주문서 여부 */
            isDiconOnce: eqPageSet(PageSet.DICON_PAYMENT),
            /** 인증형 단건 결제창 여부 */
            isPartnersOnce: eqPageSet(PageSet.PARTNERS_PAYMENT),
            /** 결제 상세 여부 */
            isPaymentDetail: eqPageSet(PageSet.PAYMENT_DETAIL),
            /** 결제창 여부 */
            isPaymentCheckoutGroup: pageSetIncludes([
                PageSet.DICON_PAYMENT,
                PageSet.PARTNERS_PAYMENT,
                PageSet.NAVER_FINANCIAL_PAYMENT,
                PageSet.NAVER_FINANCIAL_AUTO,
            ]),
            /** 포인트 받기 여부 */
            isReceivePoint: eqPageSet(PageSet.RECEIVE_POINT),
            /** 디컨 정기결제 주문서 여부 */
            isDiconAuto: eqPageSet(PageSet.DICON_AUTO),
            /** 인증형 정기결제 여부 */
            isPartnersAuto: eqPageSet(PageSet.PARTNERS_AUTO),
            /** 예약 매장 방문결제 여부 */
            isBooking: eqPageSet(PageSet.BOOKING),
            /** 결제형 정기결제 여부 */
            isNaverFinancialAuto: eqPageSet(PageSet.NAVER_FINANCIAL_AUTO),
            /** 결제형 단건결제 여부 */
            isNaverFinancialPayment: eqPageSet(PageSet.NAVER_FINANCIAL_PAYMENT),
            /** 디컨 주문서 여부 */
            isDiconGroup: pageSetIncludes([PageSet.DICON_PAYMENT, PageSet.DICON_AUTO]),
            /** 결제형 주문서 여부 */
            isNaverFinancialGroup: pageSetIncludes([PageSet.NAVER_FINANCIAL_AUTO, PageSet.NAVER_FINANCIAL_PAYMENT]),
            /** 인증형 주문서 여부 */
            isPartnersGroup: pageSetIncludes([PageSet.PARTNERS_PAYMENT, PageSet.PARTNERS_AUTO]),
            /** 결제수단 여부 */
            isPaymentMethodGroup: pageSetIncludes([PageSet.DICON_AUTO, PageSet.BOOKING, PageSet.PARTNERS_AUTO]),
            /** 정기구독 주문서 여부 */
            isShoppingSubscription: eqPageSet(PageSet.SHOPPING_SUBSCRIPTION),
            /** 결제수단만 변경하는 주문서 그룹 여부 */
            isPaymentMethodManagement: eqPageSet(
                PageSet.PAYMENT_METHOD_MANAGEMENT,
            ) /** 결제수단 등록하는 자동결제 페이지 여부 */,
            isAutoPaymentGroup: pageSetIncludes([
                PageSet.PARTNERS_AUTO,
                PageSet.NAVER_FINANCIAL_AUTO,
                PageSet.DICON_AUTO,
                PageSet.BOOKING,
                PageSet.SHOPPING_SUBSCRIPTION,
            ]),
            isSaleHistory: eqPageSet(PageSet.SALE_HISTORY),
            isSaleHistoryDetail: eqPageSet(PageSet.SALE_HISTORY_DETAIL),
            isSaleSettlement: eqPageSet(PageSet.SALE_SETTLEMENT),
            isSaleSettlementDetail: eqPageSet(PageSet.SALE_SETTLEMENT_DETAIL),
            /** C3 그룹 */
            isEasyBookingResultGroup: pageSetIncludes([
                PageSet.EASY_BOOK_RESULT,
                PageSet.LOCAL_PAY_RESULT,
                PageSet.BLOG_RESULT,
                PageSet.FLEA_MARKET_RESULT,
            ]),
            isNpayBizCheckout: eqPageSet(PageSet.NPAY_BIZ_CHECKOUT),
        }
    },
)

/** 주문서 타입 */
export const orderEntryInfoSelector = createSelector(commonSelector, ({entry}) => {
    const enEntryType = eq(entry)
    const ChannelTypeIncludes = includes(entry)

    return {
        saleChannelGroup: (function () {
            if (enEntryType(OrderEntry.CHECKOUT)) {
                return OrderSheetPageSet.MALL
            }
            if (
                enEntryType(OrderEntry.EASY_BOOKING) ||
                enEntryType(OrderEntry.LOCAL_PAY) ||
                enEntryType(OrderEntry.NAVER_BLOG) ||
                enEntryType(OrderEntry.NAVERBLOG) ||
                enEntryType(OrderEntry.FLEA_MARKET)
            ) {
                return OrderSheetPageSet.EASYBOOKING
            }
            if (enEntryType(OrderEntry.SHOPN) || enEntryType(OrderEntry.GIFTING) || enEntryType(OrderEntry.BRANCH)) {
                return OrderSheetPageSet.SELLER
            }
            if (enEntryType(OrderEntry.FAMILY_PAY)) {
                return OrderSheetPageSet.FAMILY_PAY
            }
            return null
        })(),
        /** 가맹점 주문서 여부 */
        isCheckout: enEntryType(OrderEntry.CHECKOUT),
        /** 예약 주문서 여부 */
        isEasyBooking: enEntryType(OrderEntry.EASY_BOOKING),
        /** 예약/블로그/카페 - 테이블오더(v1, v2) 여부 */
        isLocalPay: enEntryType(OrderEntry.LOCAL_PAY),
        /** 카페 주문서 여부 */
        isNaverCafe: enEntryType(OrderEntry.NAVER_CAFE) || enEntryType(OrderEntry.NAVERCAFE),
        /** 플리마켓 주문서 (안전결제 지원) 여부 */
        isFleaMarket: enEntryType(OrderEntry.FLEA_MARKET),
        /** 블로그 주문서 여부 */
        isNaverBlog: enEntryType(OrderEntry.NAVER_BLOG) || enEntryType(OrderEntry.NAVERBLOG),
        /** 스마트스토어 주문서 여부 */
        isShopN: enEntryType(OrderEntry.SHOPN),
        /** 스마트스토어-선물하기 주문서 여부 */
        isGifting: enEntryType(OrderEntry.GIFTING),
        /** 스마트스토어-장보기 주문서 여부 */
        isBranch: enEntryType(OrderEntry.BRANCH),
        /** 예약, 블로그, 카페 여부 */
        isEasyBookingGroup: ChannelTypeIncludes([
            OrderEntry.EASY_BOOKING,
            OrderEntry.LOCAL_PAY,
            OrderEntry.NAVER_CAFE,
            OrderEntry.NAVERCAFE,
            OrderEntry.NAVER_BLOG,
            OrderEntry.NAVERBLOG,
            OrderEntry.FLEA_MARKET,
        ]),
        /** 주문형 해피빈 여부 */
        isHappyBeanOrder: enEntryType(ScheduledOrderEntry.HAPPY_BEAN_ORDER),
        /** 스마트스토어 여부 */
        isSellerGroup: ChannelTypeIncludes([OrderEntry.SHOPN, OrderEntry.GIFTING, OrderEntry.BRANCH]),
        /** 배송 불가 주문서 여부 */
        isNonDeliveryOrder: ChannelTypeIncludes([
            OrderEntry.EASY_BOOKING,
            OrderEntry.LOCAL_PAY,
            // 배송지 입력불가, 이슈 없을지 확인 필요
            OrderEntry.FAMILY_PAY,
        ]),
        /** 패밀리 승인결제 여부 */
        isFamilyPay: enEntryType(OrderEntry.FAMILY_PAY),
    }
})

/**
 * 혜택계산기 사용 가능 여부 (쿠폰 자동 적용 등)
 * - 스마트스토어(+선물하기, 장보기), 예약, 로컬페이
 *
 * @link https://oss.fin.navercorp.com/payment-fe/order-web/wiki/%EC%9A%A9%EC%96%B4%EC%A7%91#discountOptimization
 */
export const discountOptimizationEnabledSelector = createSelector(
    orderEntryInfoSelector,
    ({isEasyBooking, isShopN, isGifting, isBranch, isLocalPay, isNaverBlog}) =>
        isEasyBooking || isShopN || isGifting || isBranch || isLocalPay || isNaverBlog,
)

/** API Status에 따른 scene 정보 */
export const commonSceneInfoSelector = createSelector(
    (state: RootState) => state.common.scene,
    (apiStatus) => {
        const apiStatusEqualsTo = eq(apiStatus)
        return {
            apiStatus,
            isIdle: apiStatusEqualsTo(APIStatus.IDLE),
            isFetching: apiStatusEqualsTo(APIStatus.FETCHING),
            isPending: apiStatusEqualsTo(APIStatus.IDLE) || apiStatusEqualsTo(APIStatus.FETCHING),
            isResolved: apiStatusEqualsTo(APIStatus.RESOLVED),
            isRejected: apiStatusEqualsTo(APIStatus.REJECTED),
        }
    },
)

/** 주문서 스피너 필요 여부 selector */
export const shouldOrderSpinnerSelector = createSelector(commonSceneInfoSelector, ({isFetching, isIdle}) =>
    some(eq(true), [isFetching, isIdle]),
)

/**
 * 주문 진행 단계
 */
export const orderPhaseSelector = (state: RootState) => state.common.orderPhase

/**
 * 주문 진행 단계 타입
 */
export const orderPhaseInfoSelector = createSelector(orderPhaseSelector, (orderPhase) => {
    const eqOrderPhase = eq(orderPhase)
    return {
        isCheckout: eqOrderPhase(OrderPhase.CHECKOUT),
        isResult: eqOrderPhase(OrderPhase.RESULT),
        isDetail: eqOrderPhase(OrderPhase.DETAIL),
    }
})

/** seqID */
export const seqIDSelector = (state: RootState) => state.common.seqID

export const orderSheetUrlSelector = (state: RootState) => state.common.orderSheetUrl

export const logoutReturnUrlSelector = createSelector(
    commonSelector,
    pageSetInfoSelector,
    ({seqID}, {isPartnersOnce, isPartnersAuto}) => {
        if (isPartnersAuto) {
            const {origin, search, hash} = window.location
            const subPath = '/payments/auth-pay/automatic-payments'

            return `${origin}${subPath}/${seqID}${search}${hash}`
        }

        // 외부 제휴앱에서 호출되는 인증형 주문서에서는 제휴앱에서 정의해둔 주문서 subPath를 구 주문서와 동일하게 일치시켜준다
        if (isPartnersOnce) {
            const {origin, search, hash} = window.location
            const subPath = PAYMENT_SHEET_PARTNERS_OLD_PATH

            return `${origin}${subPath}/${seqID}${search}${hash}`
        }

        return location.href
    },
)

export const trackingLogDataSelector = createSelector(
    commonSelector,
    ({pageSet, entry}): {inflow: 'd' | 'c2' | 'rsv' | 'pk'} => {
        /** inflow AU 증적에 사용하던 주문서 타입별 inflow 코드. 디컨 or 주문형에서 후불결제로 진입할 경우 분류만 존재 */
        const inflow = (function () {
            const eqPageSet = eq(pageSet)
            if (eqPageSet(PageSet.DICON_PAYMENT)) {
                return 'd'
            } else if (eqPageSet(PageSet.EASY_BOOK_CHECKOUT)) {
                if (entry === OrderEntry.EASY_BOOKING) {
                    return 'rsv'
                } else if (entry === OrderEntry.LOCAL_PAY) {
                    return 'pk'
                }
            }
            return 'c2'
        })()

        return {
            inflow,
        }
    },
)

/** 채널 정보 */
export const channelSelector = createSelector(commonSelector, ({channel}) => {
    const eqChannel = eq(channel)
    return {
        channel,
        isCashOnly: eqChannel(Channel.CASH_ONLY),
        isChargeType: eqChannel(Channel.CHARGE_TYPE),
        isPayType: eqChannel(Channel.PAY_TYPE),
    }
})

export const diconEntrySelector = createSelector(commonSelector, ({entry}) => {
    const eqEntry = eq(entry)
    const entryIncludes = includes(entry)

    return {
        isWebtoonService: eqEntry(DiconEntry.SERIES),
        isSeriesChannel: eqEntry(DiconEntry.SERIES),
        isDonationService: entryIncludes([DiconEntry.HAPPY_BEAN]),
        isGiftCard: eqEntry(DiconEntry.GIFT_CARD),
        isChzzk: eqEntry(DiconEntry.CHZZK),
    }
})

export const diconServiceCodeSelector = createSelector(commonSelector, ({entry}) => {
    // 서비스 코드. 디컨 주문서 인입 채널을 의미.
    const svcSeq = entryToServiceSequence(entry)

    return svcSeq
})

/**
 * 결제상세 Entry
 * @description 인터페이스 구조가 동일한 entry를 그룹핑하여 제공
 */
export const paymentDetailEntrySelector = createSelector(commonSelector, ({entry}) => {
    const eqEntry = eq(entry)

    return {
        /** 결제형 단건 / 결제형 자동 */
        isNormalRecurrentPaymentDetail:
            eqEntry(PaymentDetailEntry.PAYMENT_DETAIL_NORMAL) || eqEntry(PaymentDetailEntry.PAYMENT_DETAIL_RECURRENT),
        /** 인증형 단건 / 인증형 자동 */
        isAuthPaymentDetail:
            eqEntry(PaymentDetailEntry.PAYMENT_DETAIL_AUTH) ||
            eqEntry(PaymentDetailEntry.PAYMENT_DETAIL_AUTH_AUTOMATIC),
        /** 현장결제 / 자체가맹점 */
        isOfflineInstorePaymentDetail:
            eqEntry(PaymentDetailEntry.PAYMENT_DETAIL_OFFLINE) || eqEntry(PaymentDetailEntry.PAYMENT_DETAIL_INSTORE),
    }
})

export const orderEntrySelector = createSelector(commonSelector, ({entry}) => {
    const eqEntry = eq(entry)

    return {
        isBooking: eqEntry(OrderEntry.BOOKING),
        isSubscription: eqEntry(ScheduledOrderEntry.SUBSCRIPTION),
    }
})

export const familyPayRenderSelector = createSelector(commonSelector, ({entry}) => {
    return {
        /** 패밀리결제 노출필요한 주문서 여부  */
        familyPayViewable: includes(entry, [
            OrderEntry.CHECKOUT,
            OrderEntry.EASY_BOOKING,
            OrderEntry.LOCAL_PAY,
            OrderEntry.SHOPN,
            OrderEntry.BRANCH,
            OrderEntry.GIFTING,
        ]),
        /** 패밀리결제로 결제가능 주문서 여부 */
        familyPayAvailable: includes(entry, [OrderEntry.CHECKOUT, OrderEntry.SHOPN]),
    }
})

/**
 * AU 증적을 쌓은 서비스를 확인하기 위해 page field 에 서비스별로 다른 값을 증적한다.
 * @see https://oss.fin.navercorp.com/payment-fe/order-web/issues/9395
 * */
export const auLogPageFieldSeletor = createSelector(
    orderEntryInfoSelector,
    pageSetInfoSelector,
    (
        {isCheckout, isSellerGroup, isEasyBooking, isLocalPay, isNaverBlog, isFleaMarket},
        {isDiconOnce, isPartnersOnce, isPartnersAuto, isNaverFinancialPayment, isNaverFinancialAuto},
    ) => {
        if (isCheckout) {
            return 'ORDER_C1'
        } else if (isSellerGroup) {
            return 'ORDER_C2'
        } else if (isEasyBooking) {
            return 'ORDER_EASYBOOKING'
        } else if (isLocalPay) {
            return 'ORDER_LOCALPAY'
        } else if (isNaverBlog) {
            return 'ORDER_BLOG'
        } else if (
            // TODO ESCROW AU 증적 확인하기
            isFleaMarket
        ) {
            return 'ORDER_CAFE'
        } else if (isDiconOnce) {
            return 'DICON'
        } else if (isPartnersOnce) {
            return 'AUTH_PAY'
        } else if (isPartnersAuto) {
            return 'AUTH_AUTO_PAY'
        } else if (isNaverFinancialPayment) {
            return 'ONLINE_PAY'
        } else if (isNaverFinancialAuto) {
            return 'ONLINE_AUTO_PAY'
        }
        return ''
    },
)

export const languageSelector = createSelector(commonSelector, ({language}) => {
    return {
        isForeignLanguage: !!language && language !== 'ko',
        language,
    }
})
